import React, { useMemo, useState } from 'react';
import { Download, Activity, Users, Clock } from 'lucide-react';
import { 
  BarChart, Bar, XAxis, YAxis, CartesianGrid, Tooltip as RechartsTooltip, ResponsiveContainer,
  PieChart, Pie, Cell, Legend
} from 'recharts';
import { users, wbsCodes, getCompanyFromEmail } from '../dummyData';
import { downloadCSV, downloadXLSX, formatSAPDate, formatReadableDate } from '../utils/exportUtils';
import { formatNumber } from '../utils/formatUtils';

const COLORS = ['#6366f1', '#06b6d4', '#f59e0b', '#10b981', '#f43f5e'];

export default function Dashboard({ entries, activeUser, users: propUsers }) {
  const usersList = propUsers || users;
  const userRole = activeUser?.userRole || 'Normal';
  
  // Define available tabs based on role
  const tabs = [
    { id: 'personal', label: 'Personal Insight', icon: Clock, roles: ['Admin', 'Management', 'Normal'] },
    { id: 'team', label: 'Team Insight', icon: Users, roles: ['Admin', 'Management'] },
    { id: 'overview', label: 'Overview Dashboard', icon: Activity, roles: ['Admin'] },
  ].filter(tab => tab.roles.includes(userRole));
 
  const [activeTab, setActiveTab] = useState(tabs[0]?.id || 'personal');
 
  // Filter display entries based on active tab
  const displayEntries = useMemo(() => {
    if (activeTab === 'personal') {
      return entries.filter(e => e.userId === activeUser.id);
    }
    if (activeTab === 'team') {
      // Find all users who have this managerId
      const teamUserIds = usersList
        .filter(u => u.managerId === activeUser?.id || u.id === activeUser?.id)
        .map(u => u.id);
      
      return entries.filter(e => teamUserIds.includes(e.userId));
    }
    return entries; // Overview
  }, [entries, activeTab, activeUser, usersList]);

  const approvedEntries = displayEntries.filter(e => e.status === 'Approved');
  const isPersonalView = activeTab === 'personal';
  const isProjectView = activeTab === 'overview';

  // KPI: Total Hours Billed
  const totalHours = approvedEntries.reduce((sum, entry) => sum + entry.hours, 0);

  // KPI: Burn Rate by Scope
  const burnRateData = useMemo(() => {
    const scopeMap = {};
    approvedEntries.forEach(entry => {
      const wbs = wbsCodes.find(w => w.code === entry.wbsCode);
      const scope = wbs ? wbs.scope : 'Unknown';
      scopeMap[scope] = (scopeMap[scope] || 0) + entry.hours;
    });
    return Object.entries(scopeMap).map(([name, value]) => ({ name, value }));
  }, [approvedEntries]);

  // KPI: Labor Distribution (Only for Team/Overview)
  const laborDistributionData = useMemo(() => {
    const companyMap = {};
    approvedEntries.forEach(entry => {
      const user = usersList.find(u => u.id === entry.userId);
      const company = user ? getCompanyFromEmail(user.email) : 'Unknown';
      companyMap[company] = (companyMap[company] || 0) + entry.hours;
    });
    return Object.entries(companyMap).map(([name, value]) => ({ name, hours: value }));
  }, [approvedEntries]);

  const exportToSAP = () => {
    const headers = ['PERNR', 'WORKDATE', 'LSTAR', 'SKOSTL', 'SE_WBS_ELEMENT', 'CATSAMOUNT'];
    const sortedEntries = [...approvedEntries].sort((a,b) => new Date(a.date) - new Date(b.date));
    const rows = sortedEntries.map(entry => {
      const user = usersList.find(u => u.id === entry.userId);
      if(!user) return null;
      return [user.personnelNumber, formatSAPDate(entry.date), user.roleScope, user.costCenter, entry.wbsCode, entry.hours];
    }).filter(row => row !== null);
    downloadCSV(headers, rows, `SAP_CATS_Export_${formatSAPDate(new Date())}.csv`, false);
  };

  const exportToExcel = () => {
    const headers = ['Employee', 'Date', 'Activity', 'Cost Center', 'WBS Task', 'Hours'];
    const sortedEntries = [...approvedEntries].sort((a,b) => new Date(b.date) - new Date(a.date));
    const rows = sortedEntries.map(entry => {
      const user = users.find(u => u.id === entry.userId);
      if(!user) return null;
      return [user.name, formatReadableDate(entry.date), user.roleScope, user.costCenter, entry.wbsCode, entry.hours];
    }).filter(row => row !== null);
    downloadXLSX(headers, rows, `Dashboard_Entries_${formatSAPDate(new Date())}.xlsx`);
  };

  return (
    <div className="space-y-6">
      {/* Header and Tab Navigation */}
      <div className="flex flex-col lg:flex-row lg:items-end justify-between gap-6 mb-2">
        <div>
          <h2 className="text-xl sm:text-2xl font-black text-slate-800 tracking-tight">
            {activeTab === 'personal' ? 'Personal Insights' : 
             activeTab === 'team' ? 'My Project Team' : 
             'Project Overview'}
          </h2>
          <p className="text-slate-500 text-xs sm:text-sm font-medium mt-1">
            {activeTab === 'personal' ? 'Your hours and personal performance metrics.' : 
             activeTab === 'team' ? 'Consolidated metrics for your allocated team members.' : 
             'Real-time project-wide metrics and SAP extraction.'}
          </p>
        </div>

        {/* Pill Tabs */}
        {tabs.length > 1 && (
          <div className="flex p-1 bg-slate-100/80 backdrop-blur-sm rounded-xl border border-slate-200 overflow-x-auto no-scrollbar max-w-full">
            <div className="flex gap-1 min-w-max">
              {tabs.map((tab) => {
                const Icon = tab.icon;
                const isActive = activeTab === tab.id;
                return (
                  <button
                    key={tab.id}
                    onClick={() => setActiveTab(tab.id)}
                    className={`flex items-center gap-2 px-4 py-2 rounded-lg text-xs sm:text-sm font-bold transition-all duration-200 whitespace-nowrap ${
                      isActive 
                        ? 'bg-indigo-600 text-white shadow-md' 
                        : 'text-slate-500 hover:text-slate-700 hover:bg-slate-200/50'
                    }`}
                  >
                    <Icon className={`w-3.5 h-3.5 ${isActive ? 'text-white' : 'text-slate-400'}`} />
                    {tab.label}
                  </button>
                );
              })}
            </div>
          </div>
        )}
      </div>

      {/* Top KPI Cards */}
      <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
        <div className="bg-white p-6 rounded-2xl shadow-sm border border-slate-100 flex items-center justify-between transition-all hover:shadow-md">
          <div>
            <p className="text-sm font-bold text-slate-500 uppercase tracking-wider">Total Hours Billed</p>
            <p className="text-4xl font-black text-slate-800 mt-2">{formatNumber(totalHours)}</p>
          </div>
          <div className="w-14 h-14 rounded-full bg-indigo-50 flex items-center justify-center text-indigo-600">
            <Clock className="w-7 h-7" />
          </div>
        </div>
        
        <div className="bg-white p-6 rounded-2xl shadow-sm border border-slate-100 flex items-center justify-between transition-all hover:shadow-md">
          <div>
            <p className="text-sm font-bold text-slate-500 uppercase tracking-wider">
              {isPersonalView ? 'Active Scopes' : 'Active Personnel'}
            </p>
            <p className="text-4xl font-black text-slate-800 mt-2">
              {formatNumber(isPersonalView ? burnRateData.length : displayEntries.reduce((acc, e) => acc.add(e.userId), new Set()).size)}
            </p>
          </div>
          <div className="w-14 h-14 rounded-full bg-cyan-50 flex items-center justify-center text-cyan-600">
            <Users className="w-7 h-7" />
          </div>
        </div>

        <div className="bg-white p-6 rounded-2xl shadow-sm border border-slate-100 flex items-center justify-between transition-all hover:shadow-md">
          <div>
            <p className="text-sm font-bold text-slate-500 uppercase tracking-wider">Total Entries</p>
            <p className="text-4xl font-black text-slate-800 mt-2">{formatNumber(displayEntries.length)}</p>
          </div>
          <div className="w-14 h-14 rounded-full bg-emerald-50 flex items-center justify-center text-emerald-600">
            <Activity className="w-7 h-7" />
          </div>
        </div>
      </div>

      {/* Charts Section */}
      <div className={`grid grid-cols-1 ${isPersonalView ? '' : 'lg:grid-cols-2'} gap-6`}>
        {/* Burn Rate Chart */}
        <div className="bg-white p-5 sm:p-6 rounded-2xl shadow-sm border border-slate-100 min-h-[300px] sm:min-h-[350px]">
          <h3 className="text-base sm:text-lg font-black text-slate-800 mb-6 tracking-tight">Hours by WBS Scope</h3>
          <div className="h-[250px] sm:h-[280px] w-full">
            <ResponsiveContainer width="100%" height="100%">
              <BarChart data={burnRateData} margin={{ top: 5, right: 10, left: -30, bottom: 5 }}>
                <CartesianGrid strokeDasharray="3 3" vertical={false} stroke="#f1f5f9" />
                <XAxis dataKey="name" axisLine={false} tickLine={false} tick={{ fill: '#94a3b8', fontSize: 11, fontWeight: 700 }} />
                <YAxis axisLine={false} tickLine={false} tick={{ fill: '#94a3b8', fontSize: 11, fontWeight: 700 }} />
                <RechartsTooltip 
                  cursor={{ fill: '#f8fafc' }}
                  contentStyle={{ borderRadius: '12px', border: '1px solid #e2e8f0', boxShadow: '0 4px 6px -1px rgb(0 0 0 / 0.1)' }}
                />
                <Bar dataKey="value" fill="#6366f1" radius={[4, 4, 0, 0]} barSize={32} />
              </BarChart>
            </ResponsiveContainer>
          </div>
        </div>

        {/* Labor Distribution Chart - Only for non-personal views */}
        {!isPersonalView && (
          <div className="bg-white p-5 sm:p-6 rounded-2xl shadow-sm border border-slate-100 min-h-[300px] sm:min-h-[350px]">
            <h3 className="text-base sm:text-lg font-black text-slate-800 mb-6 tracking-tight">
              {activeTab === 'team' ? 'Burn Rate by Individual' : 'Labor Distribution'}
            </h3>
            <div className="h-[250px] sm:h-[280px] w-full">
              <ResponsiveContainer width="100%" height="100%">
                <PieChart>
                  <Pie
                    data={laborDistributionData}
                    cx="50%"
                    cy="50%"
                    innerRadius={60}
                    outerRadius={85}
                    paddingAngle={4}
                    dataKey="hours"
                    stroke="none"
                  >
                    {laborDistributionData.map((entry, index) => (
                      <Cell key={`cell-${index}`} fill={COLORS[index % COLORS.length]} />
                    ))}
                  </Pie>
                  <RechartsTooltip 
                    contentStyle={{ borderRadius: '12px', border: '1px solid #e2e8f0', boxShadow: '0 4px 6px -1px rgb(0 0 0 / 0.1)' }}
                  />
                  <Legend verticalAlign="bottom" height={36} iconType="circle" wrapperStyle={{ fontSize: '11px', fontWeight: 600, color: '#64748b' }}/>
                </PieChart>
              </ResponsiveContainer>
            </div>
          </div>
        )}
      </div>

      {/* Recent Entries & Export */}
      <div className="bg-white rounded-2xl shadow-sm border border-slate-100 overflow-hidden">
        <div className="p-6 border-b border-slate-100 flex flex-col sm:flex-row justify-between items-start sm:items-center gap-4 bg-slate-50/50">
          <h3 className="text-lg font-bold text-slate-800 tracking-tight">Timesheet Log</h3>
          <div className="flex gap-3">
            <button 
              onClick={exportToExcel}
              className="flex items-center gap-2 bg-white hover:bg-slate-50 text-slate-700 border border-slate-200 px-5 py-2.5 rounded-xl text-sm font-bold transition-all shadow-sm active:scale-95"
            >
              <Download className="w-4 h-4 text-indigo-500" /> Export to Excel (.xlsx)
            </button>
            {isProjectView && (
              <button 
                onClick={exportToSAP}
                className="flex items-center gap-2 bg-slate-800 hover:bg-slate-700 text-white px-5 py-2.5 rounded-xl text-sm font-bold transition-all shadow-sm active:scale-95"
              >
                <Download className="w-4 h-4" /> Export to SAP (.csv)
              </button>
            )}
          </div>
        </div>
        <div className="overflow-x-auto">
          <table className="w-full text-left border-collapse min-w-[600px]">
            <thead>
              <tr className="bg-slate-50 border-b border-slate-200">
                <th className="py-4 px-6 font-bold text-slate-500 text-xs uppercase tracking-wider">Date</th>
                <th className="py-4 px-6 font-bold text-slate-500 text-xs uppercase tracking-wider">User</th>
                <th className="py-4 px-6 font-bold text-slate-500 text-xs uppercase tracking-wider">Company</th>
                <th className="py-4 px-6 font-bold text-slate-500 text-xs uppercase tracking-wider">WBS Code</th>
                <th className="py-4 px-6 font-bold text-slate-500 text-xs uppercase tracking-wider">Status</th>
                <th className="py-4 px-6 font-bold text-slate-500 text-xs uppercase tracking-wider text-right">Hours</th>
              </tr>
            </thead>
            <tbody className="divide-y divide-slate-100">
              {displayEntries.slice(-10).reverse().map((entry, i) => {
                const u = usersList.find(x => x.id === entry.userId);
                return (
                  <tr key={i} className="hover:bg-indigo-50/30 transition-colors">
                    <td className="py-4 px-6 text-sm text-slate-600 font-semibold">{entry.date}</td>
                    <td className="py-4 px-6 text-sm text-slate-800 font-medium">{u?.name}</td>
                    <td className="py-4 px-6 text-sm">
                      <span className="inline-flex items-center px-2.5 py-1 rounded-full text-xs font-bold bg-slate-100 text-slate-700 border border-slate-200 shadow-sm">
                         {u ? getCompanyFromEmail(u.email) : 'Unknown'}
                      </span>
                    </td>
                    <td className="py-4 px-6 text-sm font-mono text-slate-600 bg-slate-50/50">{entry.wbsCode}</td>
                    <td className="py-4 px-6 text-sm">
                      <span className={`inline-flex items-center px-2 py-0.5 rounded text-xs font-bold ${
                        entry.status === 'Approved' ? 'bg-emerald-50 text-emerald-700 border border-emerald-200' :
                        entry.status === 'Rejected' ? 'bg-rose-50 text-rose-700 border border-rose-200' :
                        'bg-amber-50 text-amber-700 border border-amber-200'
                      }`}>
                        {entry.status}
                      </span>
                    </td>
                    <td className="py-4 px-6 text-sm text-indigo-700 font-black text-right text-lg">{formatNumber(entry.hours)}</td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>
        <div className="p-4 bg-slate-50 border-t border-slate-100 text-center">
            <p className="text-xs text-slate-500 font-medium uppercase tracking-wider">Showing up to 10 most recent entries</p>
        </div>
      </div>
    </div>
  );
}
